import { Navigate, useParams } from 'react-router-dom';
import { AudioProvider } from '@utils/providers/audio-context.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import DocumentNotFound from '@components/editors/document-not-found.tsx';
import SessionNotFound from '@components/editors/session-not-found.tsx';
import BlobWarmingOverlay from '@components/editors/blob-warming-overlay.tsx';
import { useGetSharedTranscriptSession } from '@utils/api/magic-links/use-get-shared-transcript-session.tsx';
import { useGetSharedDocument } from '@utils/api/magic-links/use-get-shared-document.tsx';
import SharedTranscriptMain from '@components/editors/shared-editor/shared-transcript/shared-transcript-main.tsx';
import { SharedTranscriptProvider } from '@utils/providers/shared-transcript-context.tsx';
import { SharedTranscriptHotkeyProvider } from '@utils/providers/shared-transcript-hotkey-context.tsx';
import { useCanOpenPrivateSession } from '@utils/api/magic-links/use-can-open-private-session.tsx';
import { PATHS } from '../../router/paths.ts';

type Params = {
  sessionId: string;
  documentId: string;
  token: string;
};

export default function SharedTranscript() {
  const { sessionId, documentId, token } = useParams<Params>();
  if (!token) return;
  const {
    data: session,
    isLoading: sessionIsLoading,
    error: sessionError
  } = useGetSharedTranscriptSession(sessionId, token);

  const tier = session?.result?.access_tier;
  const isWarming = tier === 'cold' || tier === 'archive';

  const {
    data: document,
    isLoading: documentIsLoading,
    error: documentError
  } = useGetSharedDocument({
    sessionId,
    documentId,
    token,
    enabled: !!sessionId && !!documentId && !!token && !isWarming
  });

  // Logged-in visitors who can open the session go to the normal editor;
  // everyone else (anonymous, or another organisation) stays here.
  const privateAccess = useCanOpenPrivateSession(sessionId);

  if (
    privateAccess.checking ||
    sessionIsLoading ||
    (!isWarming && documentIsLoading)
  ) {
    return (
      <div className="h-screen w-full flex justify-center items-center align-middle">
        <Loader />
      </div>
    );
  }

  if (privateAccess.canOpen && sessionId && documentId) {
    return <Navigate to={PATHS(sessionId, documentId).TRANSCRIPT} />;
  }

  if (sessionError || !session || !sessionId) {
    return <SessionNotFound error={sessionError} />;
  }

  if (isWarming) {
    return <BlobWarmingOverlay session={session.result} readOnly />;
  }

  if (documentError || !document || !documentId) {
    return <DocumentNotFound error={documentError} />;
  }

  return (
    <AudioProvider timecodeOffset={document.result.timecode_offset}>
      <SharedTranscriptProvider
        session={session.result}
        document={document}
        token={token}
      >
        <SharedTranscriptHotkeyProvider>
          <SharedTranscriptMain />
        </SharedTranscriptHotkeyProvider>
      </SharedTranscriptProvider>
    </AudioProvider>
  );
}
